import type {
  BboxCoord,
  BboxKeyframe,
  ReframeMeta,
  ReframeSegment,
} from '@/shared/apis/editor/reframe-types';

// ─── 결과 타입 ────────────────────────────────────────────────

/** 정규화된 crop 영역 (0~1 비율, 원본 해상도 기준) */
export interface CropRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface SingleCropResult {
  mode: 'single';
  crop: CropRect;
}

export interface DualCropResult {
  mode: 'dual';
  topCrop: CropRect;
  bottomCrop: CropRect;
}

export type ReframeCropResult = SingleCropResult | DualCropResult;

// ─── 상수 ─────────────────────────────────────────────────────

/** single 모드: 얼굴 bbox 확장 배수 (상반신 포함) */
const EXPAND_FACTOR_SINGLE = 2.5;
/** dual 모드: 더 타이트한 확장 (얼굴+어깨 정도) — 두 화자 구분을 위해 */
const EXPAND_FACTOR_DUAL = 1.5;
/** 얼굴 중심을 아래로 이동하는 비율 (상반신 포함) */
const CENTER_Y_OFFSET_RATIO = 0.3;
/** 최소 bbox 크기 (px) — 이보다 작으면 fallback */
const MIN_BBOX_SIZE = 10;

// ─── 핵심 함수 ────────────────────────────────────────────────

/**
 * 현재 시간에 해당하는 활성 세그먼트를 찾는다.
 */
export const findActiveSegment = (
  segments: ReframeSegment[],
  currentTimeMs: number,
): ReframeSegment | null => {
  for (const segment of segments) {
    if (currentTimeMs >= segment.startMs && currentTimeMs <= segment.endMs) {
      return segment;
    }
  }
  return null;
};

/**
 * keyframe 배열에서 현재 시간에 대한 bbox를 선형 보간한다.
 * keyframe 배열은 timestampMs 기준 오름차순 정렬 전제.
 */
export const interpolateBbox = (
  keyframes: BboxKeyframe[],
  currentTimeMs: number,
): BboxCoord | null => {
  if (!keyframes || keyframes.length === 0) return null;

  const first = keyframes[0]!;
  const last = keyframes[keyframes.length - 1]!;

  // keyframe이 1개면 그대로 반환
  if (keyframes.length === 1) return [...first.bbox] as BboxCoord;

  // 범위 밖: clamp
  if (currentTimeMs <= first.timestampMs) {
    return [...first.bbox] as BboxCoord;
  }
  if (currentTimeMs >= last.timestampMs) {
    return [...last.bbox] as BboxCoord;
  }

  // 이진 탐색으로 인접 keyframe 2개 찾기
  let lo = 0;
  let hi = keyframes.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (keyframes[mid]!.timestampMs <= currentTimeMs) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  const kfA = keyframes[lo]!;
  const kfB = keyframes[hi]!;
  const range = kfB.timestampMs - kfA.timestampMs;

  // 동일 시간이면 보간 불필요
  if (range <= 0) return [...kfA.bbox] as BboxCoord;

  const t = (currentTimeMs - kfA.timestampMs) / range;
  return kfA.bbox.map((v, i) => v + t * (kfB.bbox[i]! - v)) as BboxCoord;
};

/**
 * 얼굴 bbox를 상반신이 포함되도록 확장한다.
 * @param expandFactor 확장 배수 (single: 2.5, dual: 1.5)
 */
export const expandBboxToUpperBody = (
  bbox: BboxCoord,
  expandFactor: number = EXPAND_FACTOR_SINGLE,
): BboxCoord => {
  const faceW = bbox[2] - bbox[0];
  const faceH = bbox[3] - bbox[1];

  if (faceW <= 0 || faceH <= 0) return bbox;

  const cx = (bbox[0] + bbox[2]) / 2;
  const cy = (bbox[1] + bbox[3]) / 2 + faceH * CENTER_Y_OFFSET_RATIO;

  const expandedW = faceW * expandFactor;
  const expandedH = faceH * expandFactor;

  return [
    cx - expandedW / 2,
    cy - expandedH / 2,
    cx + expandedW / 2,
    cy + expandedH / 2,
  ];
};

/**
 * 확장된 bbox를 타겟 aspect ratio에 맞추고 비디오 경계 내로 clamp한다.
 * @param targetAspectRatio width/height (예: 9/16 = 0.5625)
 */
export const adjustBboxToAspectRatio = (
  bbox: BboxCoord,
  targetAspectRatio: number,
  videoWidth: number,
  videoHeight: number,
): BboxCoord => {
  let cropW = bbox[2] - bbox[0];
  let cropH = bbox[3] - bbox[1];

  if (cropW <= 0 || cropH <= 0) {
    return [0, 0, videoWidth, videoHeight];
  }

  const cx = (bbox[0] + bbox[2]) / 2;
  const cy = (bbox[1] + bbox[3]) / 2;

  const currentRatio = cropW / cropH;

  // aspect ratio 맞춤
  if (currentRatio > targetAspectRatio) {
    // 현재가 너무 넓음 → 높이를 늘림
    cropH = cropW / targetAspectRatio;
  } else {
    // 현재가 너무 좁음 → 너비를 늘림
    cropW = cropH * targetAspectRatio;
  }

  // 비디오 경계 clamp
  // 최소한 비디오 크기보다 크지 않도록
  cropW = Math.min(cropW, videoWidth);
  cropH = Math.min(cropH, videoHeight);

  // 중심 기반 좌표 계산 후 경계 조정
  let x1 = cx - cropW / 2;
  let y1 = cy - cropH / 2;
  let x2 = cx + cropW / 2;
  let y2 = cy + cropH / 2;

  // 경계 밖으로 나가면 이동
  if (x1 < 0) {
    x2 -= x1;
    x1 = 0;
  }
  if (y1 < 0) {
    y2 -= y1;
    y1 = 0;
  }
  if (x2 > videoWidth) {
    x1 -= x2 - videoWidth;
    x2 = videoWidth;
  }
  if (y2 > videoHeight) {
    y1 -= y2 - videoHeight;
    y2 = videoHeight;
  }

  // 최종 clamp
  x1 = Math.max(0, x1);
  y1 = Math.max(0, y1);
  x2 = Math.min(videoWidth, x2);
  y2 = Math.min(videoHeight, y2);

  return [x1, y1, x2, y2];
};

/**
 * bbox를 0~1 정규화된 CropRect로 변환한다.
 */
const bboxToCropRect = (
  bbox: BboxCoord,
  videoWidth: number,
  videoHeight: number,
): CropRect => {
  if (videoWidth <= 0 || videoHeight <= 0) {
    return { left: 0, top: 0, width: 1, height: 1 };
  }
  return {
    left: bbox[0] / videoWidth,
    top: bbox[1] / videoHeight,
    width: (bbox[2] - bbox[0]) / videoWidth,
    height: (bbox[3] - bbox[1]) / videoHeight,
  };
};

/**
 * 단일 track의 bbox를 보간 → 확장 → aspect ratio 맞춤 → CropRect 반환
 */
const computeCropForTrack = (
  trackId: number,
  bboxKeyframes: ReframeMeta['bboxKeyframes'],
  currentTimeMs: number,
  targetAspectRatio: number,
  videoWidth: number,
  videoHeight: number,
  expandFactor: number = EXPAND_FACTOR_SINGLE,
): CropRect | null => {
  const keyframes = bboxKeyframes[String(trackId)];
  if (!keyframes || keyframes.length === 0) return null;

  const bbox = interpolateBbox(keyframes, currentTimeMs);
  if (!bbox) return null;

  // 유효성 검증
  const faceW = bbox[2] - bbox[0];
  const faceH = bbox[3] - bbox[1];
  if (faceW < MIN_BBOX_SIZE || faceH < MIN_BBOX_SIZE) return null;

  const expanded = expandBboxToUpperBody(bbox, expandFactor);
  const adjusted = adjustBboxToAspectRatio(
    expanded,
    targetAspectRatio,
    videoWidth,
    videoHeight,
  );

  return bboxToCropRect(adjusted, videoWidth, videoHeight);
};

// ─── 메인 함수 ────────────────────────────────────────────────

export interface CalculateReframeCropParams {
  reframeMeta: ReframeMeta;
  currentTimeMs: number;
  videoWidth: number;
  videoHeight: number;
  compositionWidth: number;
  compositionHeight: number;
}

/**
 * 리프레임 crop 결과를 계산한다.
 * - single 모드: 전체 화면에 하나의 crop
 * - dual 모드: 상/하 분할로 두 개의 crop
 * - 세그먼트/keyframe 없으면 null (fallback)
 */
export const calculateReframeCrop = ({
  reframeMeta,
  currentTimeMs,
  videoWidth,
  videoHeight,
  compositionWidth,
  compositionHeight,
}: CalculateReframeCropParams): ReframeCropResult | null => {
  if (!reframeMeta.segments.length) return null;

  const segment = findActiveSegment(reframeMeta.segments, currentTimeMs);
  if (!segment) return null;

  if (segment.mode === 'dual' && segment.tracks.length >= 2) {
    // dual 모드: 상/하 각각 crop (각 영역은 compositionWidth x compositionHeight/2)
    const dualAspectRatio = compositionWidth / (compositionHeight / 2);

    const topCrop = computeCropForTrack(
      segment.tracks[0]!,
      reframeMeta.bboxKeyframes,
      currentTimeMs,
      dualAspectRatio,
      videoWidth,
      videoHeight,
      EXPAND_FACTOR_DUAL,
    );

    const bottomCrop = computeCropForTrack(
      segment.tracks[1]!,
      reframeMeta.bboxKeyframes,
      currentTimeMs,
      dualAspectRatio,
      videoWidth,
      videoHeight,
      EXPAND_FACTOR_DUAL,
    );

    if (!topCrop || !bottomCrop) return null;

    return { mode: 'dual', topCrop, bottomCrop };
  }

  // single 모드 (또는 dual이지만 track이 1개인 경우)
  const trackId = segment.tracks[0];
  if (trackId == null) return null;

  const singleAspectRatio = compositionWidth / compositionHeight;
  const crop = computeCropForTrack(
    trackId,
    reframeMeta.bboxKeyframes,
    currentTimeMs,
    singleAspectRatio,
    videoWidth,
    videoHeight,
  );

  if (!crop) return null;

  return { mode: 'single', crop };
};
